import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MessageSquareText, Sparkles, Link2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { setRemember } from "@/lib/api";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Allow callers like /delete-account to send users through login and bounce
  // them back. Only accept same-origin paths — never raw URLs.
  const returnTo = (() => {
    const raw = (searchParams.get("return") || searchParams.get("next"));
    if (raw && raw.startsWith("/") && !raw.startsWith("//")) return raw;
    return "/app";
  })();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [stayLoggedIn, setStayLoggedIn] = useState(true);

  const handleGoogle = () => {
    // Persist the user's choice before we leave the SPA — AuthCallback picks
    // it back up when it stores the token returned by Emergent auth.
    setRemember(stayLoggedIn);
    // Detect Capacitor native. On mobile, window.location.origin is
    // `https://localhost` (the WebView origin) which no external browser can
    // reach — Google's redirect would land on the phone's own localhost and
    // fail with ERR_CONNECTION_REFUSED. So we build the callback against
    // the app's actual public backend (derived from axios's baseURL, which
    // was baked in at AAB build time). Web keeps the deployment-agent-blessed
    // window.location.origin pattern.
    const isNative = !!window.Capacitor?.isNativePlatform?.();
    const publicBase = isNative
      ? (api.defaults.baseURL || "").replace(/\/api\/?$/, "")
      : window.location.origin;
    // The `?mobile=1` flag tells AuthCallback to bounce the finished token
    // back into the app via the com.notelosh.app:// deep link (see
    // AuthCallback.jsx + NativeAuthBridge.jsx + AndroidManifest intent-filter).
    const redirectUrl = `${publicBase}/auth/callback${isNative ? "?mobile=1" : ""}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password, stayLoggedIn);
        toast.success("Welcome back!");
      } else {
        await register(email, password, name || email.split("@")[0], stayLoggedIn);
        toast.success("Account created");
      }
      navigate(returnTo, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Authentication failed";
      // If trying to register with an existing email, auto-switch to login mode
      if (mode === "register" && /already registered/i.test(msg)) {
        toast.error("This email is already registered. Switched to sign-in.");
        setMode("login");
      } else if (mode === "login" && err?.response?.status === 401) {
        toast.error("Wrong email or password. If you signed up with Google, use the Google button above.");
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid md:grid-cols-2 bg-[#0A0A0A] text-[#F3F4F6]">
      {/* Left ambient panel */}
      <div className="relative hidden md:flex flex-col justify-between p-10 overflow-hidden">
        <img
          src="https://images.pexels.com/photos/5685251/pexels-photo-5685251.jpeg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A]/40 via-[#0A0A0A]/70 to-[#0A0A0A]" />
        <div className="relative z-10 flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-[#0A0A0A] border border-[#27272A] flex items-center justify-center overflow-hidden">
            <img src="/icons/logo-nl.png" alt="" className="h-7 w-7 object-contain" />
          </div>
          <span className="font-heading text-xl font-bold tracking-tight">NoteLosh</span>
        </div>
        <div className="relative z-10 space-y-6 max-w-md">
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight leading-[1.05]">
            Save the web. <span className="text-[#3B82F6]">Find it</span> in seconds.
          </h1>
          <p className="text-[#9CA3AF] text-base leading-relaxed">
            Drop links and notes into topical threads. Ask AI to surface anything later — by meaning, not just keywords.
          </p>
          <div className="space-y-3 pt-2">
            <Feature icon={<Link2 className="h-4 w-4" />} text="Auto link previews from any URL" />
            <Feature icon={<MessageSquareText className="h-4 w-4" />} text="Messenger-style threads for every topic" />
            <Feature icon={<Sparkles className="h-4 w-4" />} text="GPT-5.2 semantic search across everything" />
          </div>
        </div>
        <div className="relative z-10 text-xs text-[#6B7280]">© NoteLosh — your second brain</div>
      </div>

      {/* Right auth panel */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm space-y-7">
          <div className="md:hidden flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-[#0A0A0A] border border-[#27272A] flex items-center justify-center overflow-hidden">
              <img src="/icons/logo-nl.png" alt="" className="h-7 w-7 object-contain" />
            </div>
            <span className="font-heading text-xl font-bold">NoteLosh</span>
          </div>
          <div className="space-y-2">
            <h2 className="font-heading text-2xl font-semibold tracking-tight">
              {mode === "login" ? "Welcome back" : "Create your account"}
            </h2>
            <p className="text-sm text-[#9CA3AF]">
              {mode === "login" ? "Sign in to continue to your threads." : "Start organizing links and notes the smart way."}
            </p>
          </div>

          <Button
            type="button"
            onClick={handleGoogle}
            data-testid="login-google-btn"
            className="w-full rounded-full bg-[#1E1E1E] hover:bg-[#27272A] text-[#F3F4F6] border border-[#27272A] h-11 transition-all"
          >
            <GoogleIcon /> Continue with Google
          </Button>

          <div className="flex items-center gap-3 text-xs text-[#6B7280]">
            <div className="flex-1 h-px bg-[#27272A]" />
            or
            <div className="flex-1 h-px bg-[#27272A]" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs text-[#9CA3AF]">Name</Label>
                <Input
                  id="name"
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ada Lovelace"
                  className="bg-[#1E1E1E] border-[#27272A] h-11 rounded-xl"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs text-[#9CA3AF]">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="bg-[#1E1E1E] border-[#27272A] h-11 rounded-xl"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs text-[#9CA3AF]">Password</Label>
                {mode === "login" && (
                  <button
                    type="button"
                    data-testid="forgot-password-link"
                    onClick={() => setForgotOpen(true)}
                    className="text-[11px] text-[#3B82F6] hover:text-[#60A5FA] transition-colors"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                data-testid="auth-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="bg-[#1E1E1E] border-[#27272A] h-11 rounded-xl"
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-[#9CA3AF] select-none cursor-pointer">
              <Checkbox
                id="stay-logged-in"
                data-testid="stay-logged-in-checkbox"
                checked={stayLoggedIn}
                onCheckedChange={(v) => setStayLoggedIn(v === true)}
                className="border-[#3F3F46] data-[state=checked]:bg-[#2563EB] data-[state=checked]:border-[#2563EB]"
              />
              <span>Stay logged in on this device</span>
            </label>
            <Button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-btn"
              className="w-full rounded-full h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium transition-all glow-blue"
            >
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="text-center text-sm text-[#9CA3AF]">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              type="button"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              data-testid="auth-toggle-mode-btn"
              className="text-[#3B82F6] hover:text-[#60A5FA] font-medium transition-colors"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </div>

          <Link to="/" className="block text-center text-xs text-[#6B7280] hover:text-[#9CA3AF] transition-colors">
            ← Back to home
          </Link>

          <div className="flex items-center justify-center gap-3 text-[11px] text-[#6B7280] pt-1">
            <Link
              to="/terms"
              data-testid="login-terms-link"
              className="hover:text-[#9CA3AF] transition-colors"
            >
              Terms of Service
            </Link>
            <span className="text-[#27272A]">·</span>
            <Link
              to="/privacy"
              data-testid="login-privacy-link"
              className="hover:text-[#9CA3AF] transition-colors"
            >
              Privacy
            </Link>
          </div>
        </div>
      </div>

      <ForgotPasswordDialog
        open={forgotOpen}
        onOpenChange={setForgotOpen}
        defaultEmail={email}
      />
    </div>
  );
}

function Feature({ icon, text }) {
  return (
    <div className="flex items-center gap-3 text-sm text-[#D1D5DB]">
      <span className="h-7 w-7 rounded-lg bg-[#1E1E1E] border border-[#27272A] flex items-center justify-center text-[#3B82F6]">
        {icon}
      </span>
      {text}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.3 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.3 29.4 4 24 4 16.3 4 9.6 8.4 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.4l-6.3-5.3C29.3 35 26.8 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.5 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.3 5.6l6.3 5.3C40.9 36.3 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z"/>
    </svg>
  );
}
